// REST client for the chopper-report Cloudflare Worker API.
// Replaces the old direct-Firestore database-client. The legacy `states()` /
// `allStates()` polling-diff engine was removed with the pre-migration map
// surfaces (2026-07); live surfaces poll `getActiveHovers`/`getLiveAircraft`.

/**
 * @typedef {'law_enforcement'|'news_media'|'medical'|'fire_sar'|'military'|'gov_other'|'commercial'|'private'|'unknown'} Category
 */

/**
 * @typedef {'hover'|'orbit'|'traffic_pattern'|'transit'|'unclassified'} EventType
 */

/**
 * @typedef {Object} AircraftLabel
 * @property {Category} category
 * @property {string} [operator]
 * @property {'confirmed'|'inferred'|'guessed'} confidence
 * @property {'allowlist'|'pattern'|'llm'} source
 */

/**
 * @typedef {Object} AircraftState
 * @property {string} icao24
 * @property {Category} [primaryCategory] — added by Worker; defaults to 'unknown' when missing.
 */

/**
 * @typedef {Object} AircraftMetadata
 * @property {string} icao24
 * @property {AircraftLabel[]} [labels] — identity labels; empty when unclassified.
 */

/**
 * @typedef {Object} HoverEvent
 * @property {string} icao24
 * @property {EventType} eventType — populated for new events; backfilled events default to 'unclassified'.
 * @property {Category} [primaryCategory] — added by Worker; defaults to 'unknown' when missing.
 */

/**
 * @typedef {Object} ReplayPayload
 * @property {Array<[number, number]>} points  // [lat, lng] pairs, ≤120
 * @property {number[]} times                   // unix seconds, parallel to points
 * @property {[number, number, number, number]} bbox // [minLon, minLat, maxLon, maxLat]
 */

/**
 * @typedef {Object} EventDetailResponse
 * @property {HoverEvent} event
 * @property {AircraftMetadata} aircraft
 * @property {ReplayPayload | null} replay
 * @property {{geohash5: string, centerLat: number, centerLon: number, label: string | null}} region
 * @property {number} nearbyEventCount
 */

/**
 * @typedef {Object} RapSheetTotals
 * @property {number} events
 * @property {number} hoverTimeS
 * @property {number} nightEvents
 * @property {number} daytimeEvents
 * @property {Record<EventType, number>} eventsByType
 */

/**
 * @typedef {Object} RapSheetDailyBucket
 * @property {string} date            // YYYY-MM-DD in America/New_York
 * @property {number} events
 * @property {number} hoverTimeS
 */

/**
 * @typedef {Object} RapSheetRegionBreakdownEntry
 * @property {string} geohash5
 * @property {{geohash5:string, centerLat:number, centerLon:number, bboxKm:number, label:string|null, slug:string|null, name:string|null, borough:string|null}} region
 * @property {number} events
 * @property {number} hoverTimeS
 * @property {number} lastSeenAt   // unix seconds
 */

/**
 * @typedef {Object} RapSheetResponse
 * @property {AircraftMetadata} aircraft
 * @property {{geohash5:string, centerLat:number, centerLon:number, bboxKm:number, label:string|null}|null} region — null when querying without a geohash (global view).
 * @property {{days:number, sinceTs:number, untilTs:number}} window
 * @property {RapSheetTotals & {uniqueRegions?:number}} totals
 * @property {number[]} hourly                 // length 24
 * @property {RapSheetDailyBucket[]} daily     // ascending by date
 * @property {Array<{id:string, startTime:number, endTime:number, hoverTimeS:number, eventType:EventType, lat:number, lon:number, geohash5?:string}>} events
 * @property {RapSheetRegionBreakdownEntry[]} [regionBreakdown] — only populated in global mode.
 */

/**
 * @typedef {Object} AircraftLeaderboardEntry
 * @property {string} icao24
 * @property {string|null} registration
 * @property {string|null} operator
 * @property {Category} primaryCategory
 * @property {number} eventCount
 * @property {number} hoverTimeS
 * @property {string|null} topGeohash5
 */

/**
 * @typedef {Object} AircraftLeaderboardResponse
 * @property {{days:number, sinceTs:number, untilTs:number}} window
 * @property {string|null} geohash
 * @property {AircraftLeaderboardEntry[]} aircraft
 */

/**
 * @typedef {Object} RegionLeaderboardEntry
 * @property {string} geohash5
 * @property {number} centerLat
 * @property {number} centerLon
 * @property {string|null} label
 * @property {string|null} slug
 * @property {number} eventCount
 * @property {number} hoverTimeS
 * @property {number} uniqueAircraft
 */

/**
 * @typedef {Object} RegionLeaderboardResponse
 * @property {{days:number, sinceTs:number, untilTs:number}} window
 * @property {string|null} geohash
 * @property {RegionLeaderboardEntry[]} regions
 */

/**
 * @typedef {Object} HeatmapCell
 * @property {string} geohash6
 * @property {number} centerLat
 * @property {number} centerLon
 * @property {number} eventCount
 * @property {number} hoverTimeS
 * @property {string|null} slug
 */

/**
 * @typedef {Object} RegionSummary
 * @property {{geohash5:string, centerLat:number, centerLon:number, label:string|null, slug:string|null, name:string|null, borough:string|null}} region
 * @property {{days:number, sinceTs:number, untilTs:number}} window
 * @property {Object} totals
 * @property {number[]} hourly
 * @property {Array<Object>} daily
 * @property {Array<Object>} topAircraft
 * @property {Array<Object>} events
 */

/**
 * @typedef {Object} HeatmapResponse
 * @property {{days:number, sinceTs:number, untilTs:number}} window
 * @property {string|null} geohash
 * @property {HeatmapCell[]} cells
 */

// CRA replaces process.env.REACT_APP_API_URL with a string literal at build
// time. In the browser `process` itself is not defined at runtime, so do not
// guard this with `typeof process !== 'undefined'` — the guard would evaluate
// before the replaced literal is reached.
const CONFIGURED_API_URL = process.env.REACT_APP_API_URL || '';

function resolveApiUrl() {
  if (CONFIGURED_API_URL) return CONFIGURED_API_URL.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}/api`;
  }
  return '/api';
}

async function getJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`${response.status} ${response.statusText}: ${url} ${text}`);
  }
  return response.json();
}

class API {
  constructor(options = {}) {
    this.url = options.apiUrl || resolveApiUrl();
    // Metadata cache: icao24 -> Promise<metadata>.
    this._metadataCache = new Map();
  }

  // ---- Public API ----------------------------------------------------------

  async getFlight(flightID) {
    const { flight } = await getJson(
      `${this.url}/flights/${encodeURIComponent(flightID)}`,
    );
    return flight;
  }

  async history(icao24, begin, end) {
    const qs = new URLSearchParams({ begin: String(begin), end: String(end) });
    const { states } = await getJson(
      `${this.url}/states/${encodeURIComponent(icao24)}/history?${qs.toString()}`,
    );
    return states;
  }

  async historicStates(location, radius, begin, end) {
    const qs = new URLSearchParams({
      lat: String(location.latitude),
      lon: String(location.longitude),
      radius: String(radius),
      begin: String(begin),
      end: String(end),
    });
    const { states } = await getJson(`${this.url}/states/historic?${qs.toString()}`);
    return states;
  }

  async lastState(icao24) {
    const { state } = await getJson(
      `${this.url}/states/${encodeURIComponent(icao24)}/last`,
    );
    return state;
  }

  /**
   * Fetch aircraft metadata. The Worker response includes `labels`
   * (AircraftLabel[]) — empty when no identity classification is available.
   *
   * @param {{icao24:string}} args
   * @returns {Promise<AircraftMetadata>}
   */
  async aircraft({ icao24 }) {
    const { aircraft } = await getJson(
      `${this.url}/aircraft?icao24=${encodeURIComponent(icao24)}`,
    );
    return aircraft;
  }

  async route({ callsign }) {
    const { route } = await getJson(
      `${this.url}/aircraft/${encodeURIComponent(callsign)}/route`,
    );
    return route;
  }

  async metadata({ icao24 }) {
    const cached = this._metadataCache.get(icao24);
    if (cached) return cached;
    const promise = getJson(
      `${this.url}/aircraft/${encodeURIComponent(icao24)}/metadata`,
    );
    this._metadataCache.set(icao24, promise);
    promise.catch(() => this._metadataCache.delete(icao24));
    return promise;
  }

  /**
   * Hover events bounded by location/radius. Each event carries `eventType`
   * (EventType) and `primaryCategory` (Category) added by the Worker.
   *
   * @param {{ latitude: number, longitude: number }} location
   * @param {number} radius
   * @param {number} since
   * @param {EventType[]|string} [types] — optional CSV or array filter (e.g.
   *   ['hover','orbit']). When omitted, the Worker default
   *   (hover, orbit, traffic_pattern) applies.
   * @returns {Promise<HoverEvent[]>}
   */
  async hoveringHistory(location, radius, since, types) {
    const qs = new URLSearchParams({
      lat: String(location.latitude),
      lon: String(location.longitude),
      radius: String(radius),
      since: String(since),
    });
    if (types != null) {
      const value = Array.isArray(types) ? types.join(',') : String(types);
      if (value) qs.set('types', value);
    }
    const { events } = await getJson(`${this.url}/hover-events?${qs.toString()}`);
    return events;
  }

  /**
   * Hover events. Each event carries `eventType` (EventType) and
   * `primaryCategory` (Category) added by the Worker.
   *
   * @param {string|null} icao24
   * @param {number} [since]
   * @param {EventType[]|string} [types] — optional CSV or array filter (e.g.
   *   ['hover','orbit']). When omitted, the Worker default
   *   (hover, orbit, traffic_pattern) applies.
   * @returns {Promise<HoverEvent[]>}
   */
  async hoverEvents(icao24, since, types) {
    const qs = new URLSearchParams();
    if (since != null) qs.set('since', String(since));
    if (types != null) {
      const value = Array.isArray(types) ? types.join(',') : String(types);
      if (value) qs.set('types', value);
    }
    if (!icao24) {
      // Global recent — used by the Map heatmap. Capped server-side.
      const { events } = await getJson(`${this.url}/hover-events?${qs.toString()}`);
      return events;
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const { events } = await getJson(
      `${this.url}/hover-events/${encodeURIComponent(icao24)}${suffix}`,
    );
    return events;
  }

  /**
   * Fetch the full event-detail payload (event + aircraft + replay + region +
   * nearby count) by event id. Backs the v2 event detail page.
   *
   * @param {string} id — hover_events row id (`${icao24}:${startTime}`).
   * @returns {Promise<EventDetailResponse>}
   */
  async getEventById(id) {
    return getJson(`${this.url}/hover-events/by-id/${encodeURIComponent(id)}`);
  }

  /**
   * Fetch the aircraft "rap sheet" — totals, hourly histogram, daily buckets,
   * and event list for a given aircraft. Pass a geohash5 to scope to a region,
   * or null/undefined for the global view.
   *
   * @param {string} icao24
   * @param {string|null} geohash5 — 5-char geohash for scoped view, or null for the global view.
   * @param {number} [days=30]
   * @returns {Promise<RapSheetResponse>}
   */
  async getRapSheet(icao24, geohash5, days = 30) {
    const qs = new URLSearchParams({ days: String(days) });
    if (geohash5) qs.set('geohash', geohash5);
    return getJson(
      `${this.url}/aircraft/${encodeURIComponent(icao24)}/rap-sheet?${qs.toString()}`,
    );
  }

  /**
   * @param {{days?: number, geohash?: string|null, limit?: number}} [opts]
   * @returns {Promise<AircraftLeaderboardResponse>}
   */
  async getAircraftLeaderboard(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.days != null) qs.set('days', String(opts.days));
    if (opts.geohash) qs.set('geohash', opts.geohash);
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    const url = qs.toString()
      ? `${this.url}/leaderboards/aircraft?${qs.toString()}`
      : `${this.url}/leaderboards/aircraft`;
    return getJson(url);
  }

  /**
   * @param {{days?: number, geohash?: string|null, limit?: number}} [opts]
   * @returns {Promise<RegionLeaderboardResponse>}
   */
  async getRegionLeaderboard(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.days != null) qs.set('days', String(opts.days));
    if (opts.geohash) qs.set('geohash', opts.geohash);
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    const url = qs.toString()
      ? `${this.url}/leaderboards/regions?${qs.toString()}`
      : `${this.url}/leaderboards/regions`;
    return getJson(url);
  }

  /**
   * Live rotorcraft snapshot — the most-recent ADS-B state per helicopter
   * (emitter category A7) seen in the trailing window. Used to render live
   * choppers on the Discover/Region maps and a "live now" indicator on
   * aircraft pages.
   *
   * @param {{withinSeconds?: number, geohash?: string|null, icao24?: string|null}} [opts]
   * @returns {Promise<{aircraft: Array<{icao24:string, callsign:string|null, time:number, latitude:number|null, longitude:number|null, baro_altitude:number|null, velocity:number|null, true_track:number|null, vertical_rate:number|null, geohash:string, registration:string|null, typecode:string|null, owner:string|null}>, withinSeconds:number}>}
   */
  async getLiveAircraft(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.withinSeconds != null) qs.set('withinSeconds', String(opts.withinSeconds));
    if (opts.geohash) qs.set('geohash', opts.geohash);
    if (opts.icao24) qs.set('icao24', opts.icao24);
    const url = qs.toString()
      ? `${this.url}/live-aircraft?${qs.toString()}`
      : `${this.url}/live-aircraft`;
    return getJson(url);
  }

  /**
   * @param {{withinSeconds?: number, geohash?: string|null}} [opts]
   * @returns {Promise<{events: HoverEvent[]}>}
   */
  async getActiveHovers(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.withinSeconds != null) qs.set('withinSeconds', String(opts.withinSeconds));
    if (opts.geohash) qs.set('geohash', opts.geohash);
    const url = qs.toString()
      ? `${this.url}/active-hovers?${qs.toString()}`
      : `${this.url}/active-hovers`;
    return getJson(url);
  }

  /**
   * @param {{days?:number, geohash?:string|null, icao24?:string|null, limit?:number}} [opts]
   * @returns {Promise<HeatmapResponse>}
   */
  async getHeatmap(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.days != null) qs.set('days', String(opts.days));
    if (opts.geohash) qs.set('geohash', opts.geohash);
    if (opts.icao24) qs.set('icao24', opts.icao24);
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    const url = qs.toString()
      ? `${this.url}/heatmap?${qs.toString()}`
      : `${this.url}/heatmap`;
    return getJson(url);
  }

  /**
   * @param {string} geohash5
   * @param {{days?: number}} [opts]
   * @returns {Promise<RegionSummary>}
   */
  async getRegionSummary(geohash5, opts = {}) {
    const qs = new URLSearchParams();
    if (opts.days != null) qs.set('days', String(opts.days));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return getJson(
      `${this.url}/regions/${encodeURIComponent(geohash5)}/summary${suffix}`,
    );
  }

  /**
   * @param {string} slug
   * @param {{days?: number}} [opts]
   * @returns {Promise<RegionSummary>}
   */
  async getRegionSummaryBySlug(slug, opts = {}) {
    const qs = new URLSearchParams();
    if (opts.days != null) qs.set('days', String(opts.days));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return getJson(
      `${this.url}/regions/by-slug/${encodeURIComponent(slug)}/summary${suffix}`,
    );
  }
}

export { API };
export default API;
